import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore } from "./store/auth.store";
import { authApi } from "./api/auth.api";
import { Shell } from "./components/layout/Shell";
import { Login } from "./pages/Login";
import { PostLogin } from "./pages/PostLogin";
import { Dashboard } from "./pages/Dashboard";
import { AppList } from "./pages/apps/AppList";
import { AppCreate } from "./pages/apps/AppCreate";
import { AppDetail } from "./pages/apps/AppDetail";
import { AppEdit } from "./pages/apps/AppEdit";
import { AdminUsers } from "./pages/admin/Users";
import { AdminUserDetail } from "./pages/admin/UserDetail";
import { AdminBackups } from "./pages/admin/Backups";
import { AdminIssueTypes } from "./pages/admin/IssueTypes";
import { IssueList } from "./pages/issues/IssueList";
import { SubmitIssue } from "./pages/issues/SubmitIssue";
import { IssueDetail } from "./pages/issues/IssueDetail";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  if (!accessToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function useHydrateUser() {
  const { accessToken, user, setUser } = useAuthStore();
  useEffect(() => {
    if (accessToken && !user) {
      authApi.me().then(setUser).catch(() => undefined);
    }
  }, [accessToken, user, setUser]);
}

export default function App() {
  useHydrateUser();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/post-login" element={<PostLogin />} />
      <Route
        path="/"
        element={
          <AuthGuard>
            <Shell />
          </AuthGuard>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="apps"
          element={
            <AdminGuard>
              <AppList />
            </AdminGuard>
          }
        />
        <Route
          path="apps/new"
          element={
            <AdminGuard>
              <AppCreate />
            </AdminGuard>
          }
        />
        <Route
          path="apps/:appId"
          element={
            <AdminGuard>
              <AppDetail />
            </AdminGuard>
          }
        />
        <Route
          path="apps/:appId/edit"
          element={
            <AdminGuard>
              <AppEdit />
            </AdminGuard>
          }
        />
        <Route
          path="admin/users"
          element={
            <AdminGuard>
              <AdminUsers />
            </AdminGuard>
          }
        />
        <Route
          path="admin/backups"
          element={
            <AdminGuard>
              <AdminBackups />
            </AdminGuard>
          }
        />
        <Route
          path="admin/issue-types"
          element={
            <AdminGuard>
              <AdminIssueTypes />
            </AdminGuard>
          }
        />
        <Route
          path="admin/users/:userId"
          element={
            <AdminGuard>
              <AdminUserDetail />
            </AdminGuard>
          }
        />
        <Route path="issues" element={<IssueList />} />
        <Route path="issues/new" element={<SubmitIssue />} />
        <Route path="issues/:issueId" element={<IssueDetail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
